import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthenticationCard } from '@components/atoms/AuthenticationCard';
import { Button, ButtonMode } from '@components/atoms/Button';
import { Input, InputCheckRules, InputStates, InputTypes, InputVariants } from '@components/atoms/Input';
import { Space } from '@components/atoms/Space';
import { Form } from '@components/atoms/Form';
import { SITE_URLS } from '@constants';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { useNotifications } from '@hooks/useNotifications';
import { useCompleteResetPassword } from '@services/useCompleteResetPassword';
import { useLocation } from 'react-router';
import { useCheckVerificationToken } from '@services/useCheckVerificationToken';
import { Loader } from '@components/molecules/Pagination';

export const SetNewPasswordCard: FC = () => {
    const { navigate } = useCustomNavigate();
    const { showDefaultNotification } = useNotifications();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const email = params.get('email');
    const token = params.get('token');
    const { request: requestCheckVerificationToken, loading, errorMessage, lastResponseTime } = useCheckVerificationToken();
    const { t } = useTranslation();

    const translations = {
        title: t('set-new-password-card-component.title'),
        errorInvalidToken: t('set-new-password-card-component.error.invalid-token'),
    };

    useEffect(() => {
        if (email && token) {
            requestCheckVerificationToken({ email: email, token: token });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [email, token]);

    useEffect(() => {
        if (lastResponseTime) {
            if (errorMessage) {
                showDefaultNotification({ type: 'error', message: translations.errorInvalidToken });
                navigate(`/${SITE_URLS.recoverPassword}`);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime]);

    return (
        <AuthenticationCard title={translations.title} subtitle="">
            {loading ? <Loader /> : <SetNewPasswordForm />}
        </AuthenticationCard>
    );
};

interface SetNewPasswordFormErrors {
    password: 'valid' | 'invalid' | null;
    confirmPassword: string[];
}

const SetNewPasswordForm: FC = () => {
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const email = params.get('email');
    const token = params.get('token');
    const { navigate } = useCustomNavigate();
    const { showDefaultNotification } = useNotifications();
    const {
        request: requestCompleteResetPassword,
        success: successMessage,
        loading,
        error: errorCompleteResetPassword,
        lastResponseTime,
    } = useCompleteResetPassword();
    const { t } = useTranslation();

    const formRef = useRef<HTMLFormElement>(null);
    const [password, setPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [validPassword, setValidPassword] = useState<boolean>(false);

    const translations = {
        emailLabel: t('set-new-password-card-component.email.label'),
        passwordLabel: t('set-new-password-card-component.password.label'),
        confirmPasswordLabel: t('set-new-password-card-component.confirm-password.label'),
        resetPasswordButton: t('set-new-password-card-component.reset-password.button'),
        confirmPasswordRequiredError: t('set-new-password-card-component.confirm-password.required-error'),
        confirmPasswordMatchError: t('set-new-password-card-component.confirm-password.match-error'),
    };

    // errors to be displayed in the form
    const [errors, setErrors] = useState<SetNewPasswordFormErrors>({
        password: null,
        confirmPassword: [],
    });

    const handleOnClickResetPassword = () => {
        const errors = validateForm();

        if (errors.password === 'valid' && errors.confirmPassword.length === 0) {
            formRef?.current?.requestSubmit();
        }
    };

    const validateForm: () => SetNewPasswordFormErrors = () => {
        const newErrors: SetNewPasswordFormErrors = {
            password: validPassword ? 'valid' : 'invalid',
            confirmPassword: [],
        };

        // confirm password
        !confirmPassword.trim() && newErrors.confirmPassword.push(translations.confirmPasswordRequiredError);
        newErrors.confirmPassword.length === 0 &&
            password !== confirmPassword &&
            newErrors.confirmPassword.push(translations.confirmPasswordMatchError);

        setErrors(newErrors);

        return newErrors;
    };

    const submitForm: () => void = () => {
        requestCompleteResetPassword({ token: token || '', new_password: password });
    };

    // if reset was successful, go to homepage
    // if not, show error
    useEffect(() => {
        if (lastResponseTime) {
            if (errorCompleteResetPassword) {
                setErrors({ ...errors, confirmPassword: [errorCompleteResetPassword] });
            } else {
                // dispatch notification
                showDefaultNotification({ type: 'success', message: successMessage || '' });
                navigate(`/${SITE_URLS.login}`);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime]);

    return (
        <Form
            formRef={formRef}
            classes="d-flex flex-column"
            onEnterPress={handleOnClickResetPassword}
            onSubmit={() => submitForm()}>
            <>
                <Input
                    type={InputTypes.email}
                    variant={InputVariants.labeledInput}
                    label={translations.emailLabel}
                    value={email || ''}
                    onChange={() => {}}
                    disabled={true}
                />
                <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                <Input
                    type={InputTypes.password}
                    variant={InputVariants.labeledInput}
                    label={`${translations.passwordLabel}*`}
                    value={password}
                    onChange={(value) => setPassword(value)}
                    state={errors.password === 'invalid' ? InputStates.error : undefined}
                    checkRules={InputCheckRules.passwordBuild}
                    onValidationChange={setValidPassword}
                />
                <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                <Input
                    type={InputTypes.password}
                    variant={InputVariants.labeledInput}
                    label={`${translations.confirmPasswordLabel}*`}
                    value={confirmPassword}
                    onChange={(value) => setConfirmPassword(value)}
                    state={errors.confirmPassword.length > 0 ? InputStates.error : undefined}
                    errorMessages={errors.confirmPassword}
                />

                <Space spaceBreakpoints={{ xs: { space: 15 }, md: { space: 30 } }} />

                {/* reset password */}
                <Button
                    label={translations.resetPasswordButton}
                    classes="d-block"
                    mode={ButtonMode.primary}
                    onClick={() => handleOnClickResetPassword()}
                    disabled={loading}
                />
            </>
        </Form>
    );
};
